/* eslint-disable no-script-url */

import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cn from 'classnames';
import { createConsumer, getConfig } from '@rails/actioncable';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { syncWorkspaceNotificationsAction } from 'Shared/actions/workspaces';
import { useLocation } from 'react-router';
import { Feature } from 'Shared/types/Organization.type';

import { ESSENTIALS_TOOLTIP } from 'Shared/constants/PerformEssentials.constants';
import useWindowDimensions from 'Wpds/hooks/useWindowDimensions';
import ExpandNavbarContext from 'Shared/contexts/ExpandNavbarContext';
import { useLocalStorage, useFetchWorkspaces, useFeature } from 'Shared/utils/hooks';
import { getNotifications, getWorkspaceNotificationCards } from 'Shared/actions/notifications';
import PageUrls from 'Shared/constants/PageUrls.constants';
import { useOrganization } from 'Shared/utils/hooks/use-organization';
import { useSmac } from 'Shared/utils/hooks/use-smac';

import PILinkHeader from './PILinkHeader/PILinkHeader';
import ContentHeader from './ContentHeader/ContentHeader';
import SideNavigationLink from './SideNavigationLink/SideNavigationLink';
import SyncMeetingsModalHandler from './SyncMeetingsModalHandler/SyncMeetingsModalHandler';
import NewMeetingsModalHandler from './NewMeetingsModalHandler/NewMeetingsModalHandler';
import KudosNotificationModule from './KudosNotificationModule/KudosNotificationModule';
import InviteTeammatesButton from './InviteTeammatesButton/InviteTeammatesButton';
import NotificationsWrapper from './Notifications/NotificationsWrapper';

import CreateWorkspaceButton from 'Shared/components/CreateWorkspaceButton/CreateWorkspaceButton';
import { trackEvent } from 'Shared/tracking/tracking';

import TeamGroupSection from '../TeamGroupSection/TeamGroupSection';
import HomePageV2Link from '../HomePageV2Link/HomePageV2Link';
import SearchButton from './SearchButton/SearchButton';
import SettingsMenuItem from './SettingsMenuItem/SettingsMenuItem';
import ReviewsMenuItem from './ReviewsMenuItem/ReviewsMenuItem';
import BillingPlanTimerPopover from './BillingPlanTimerPopover/BillingPlanTimerPopover';
import TodayScheduleNavLink from './TodayScheduleNavLink/TodayScheduleNavLink';
import { useResponsiveScreenWidth } from 'Wpds/hooks/useResponsiveScreenWidth';
import ThirdPartyUserIndicator from './ThirdPartyUserIndicator/ThirdPartyUserIndicator';

import QuickStartGuide from 'Dashboard/components/QuickStartGuide/QuickStartGuide';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Import CSS
import './SideNavigation.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

type Props = {
  isNavbarExpanded: boolean;
  navigateOrGateFeature: Function;
  showFeature: Function;
  onCloseNavbar: Function;
  setIsWpSideNavigationExpanded: Function;
};

const cable = createConsumer(getConfig('url'));

function SideNavigation({
  isNavbarExpanded,
  navigateOrGateFeature,
  showFeature,
  onCloseNavbar,
  setIsWpSideNavigationExpanded,
}: Props) {
  const { isFreePlan: isGoalsFreePlan, isFreeTrial: isGoalsFreeTrial } = useFeature(Feature.goals);
  const { isPiDemoAccount } = useOrganization();
  const { isPartnerAccount } = useSmac();
  const { isLarge, isXtraLarge } = useResponsiveScreenWidth({
    isAtLeast: true,
    breakpoints: { lg: 1100, xl: 1360 },
  });

  const isGoalsLocked = isGoalsFreePlan || isGoalsFreeTrial || isPiDemoAccount || isPartnerAccount;

  const { width: windowWidth } = useWindowDimensions();
  const dispatch = useDispatch();
  const navbarRef = useRef(null);
  const [isExpanded, setIsExpanded] = useLocalStorage('isWpSideNavigationExpanded', true);
  const { user } = useCurrentUser();
  const userId = user.id;

  const personId = useSelector(state => state.user?.person?.id);
  const {
    newMeetingsModal: newMeetingsModalFlag,
    coaching: showCoachingMenuItem,
    capturePrototype: surfaceFlag,
    featureFlagAiToolkit: showAiToolkit,
    enablePerformanceReviews: showReviewsTab,
  } = useFlags();
  const { pathname } = useLocation();
  const showSurfaceAIItem = surfaceFlag && windowWidth > 1048;
  const pathAllowsNewMeetingsModal = pathname === '/' || pathname === '/team';

  const { isZoom, isTeamsEmbeddedTab } = useApp();

  const createWorkspaceNotificationChannel = useCallback(
    () =>
      cable.subscriptions.create(
        { channel: 'WorkspaceNotificationChannel', user_id: userId },
        {
          received: ({ data }) => dispatch(syncWorkspaceNotificationsAction(data)),
        }
      ),
    [userId, dispatch]
  );

  const createWebNotificationChannel = useCallback(
    () =>
      cable.subscriptions.create(
        { channel: 'WebNotificationChannel', person_id: personId },
        {
          received: ({ data }) =>
            dispatch({
              type: 'NOTIFICATIONS_SYNC',
              payload: data.notifications,
            }),
        }
      ),
    [dispatch, personId]
  );

  const displayBrowserNotification = data => {
    const notification = new Notification(data.title, {
      body: data.body,
      tag: data['uuid'],
      icon: '/logo_128_by_128.png',
      badge: '/logo_128_by_128.png',
    });
    trackEvent('Browser notification delivered', {
      title: data.title,
      body: data.body,
      url: data.url,
    });
    if (data.url) {
      notification.onclick = e => {
        e.preventDefault();
        trackEvent('Clicked on browser notification', {
          title: data.title,
          body: data.body,
          url: data.url,
        });
        window.open(data.url);
      };
    }
  };

  const createBrowserNotificationChannel = useCallback(
    () =>
      cable.subscriptions.create(
        { channel: 'BrowserNotificationChannel', user_id: userId },
        {
          received: data => displayBrowserNotification(data),
        }
      ),
    [userId]
  );

  useEffect(() => {
    const workspaceNotificationChannel = createWorkspaceNotificationChannel();
    const webNotificationChannel = createWebNotificationChannel();
    const browserNotificationChannel = createBrowserNotificationChannel();

    return () => {
      if (workspaceNotificationChannel) workspaceNotificationChannel.unsubscribe();
      if (webNotificationChannel) webNotificationChannel.unsubscribe();
      if (browserNotificationChannel) browserNotificationChannel.unsubscribe();
    };
  }, [createWebNotificationChannel, createWorkspaceNotificationChannel, createBrowserNotificationChannel]);

  useEffect(() => {
    dispatch(getNotifications());
    dispatch(getWorkspaceNotificationCards());
  }, [dispatch]);

  const [isWorkspaceFetching] = useFetchWorkspaces();

  const handleRecommendationsAnalyticsClick = useCallback(
    event => navigateOrGateFeature(event, 'recommendation_analytics'),
    [navigateOrGateFeature]
  );

  useEffect(() => {
    if (isNavbarExpanded) setIsExpanded(true);
  }, [isNavbarExpanded, setIsExpanded]);

  useEffect(() => {
    setIsWpSideNavigationExpanded(isExpanded);
  }, [isExpanded, setIsWpSideNavigationExpanded]);

  return (
    <ExpandNavbarContext.Provider value={isExpanded}>
      <nav
        ref={navbarRef}
        className={cn('wp-sidebar sidebar navbar-light flex-shrink-0', {
          'wp-sidebar--expanded': isExpanded,
          'wp-sidebar-device-show': isNavbarExpanded,
        })}
      >
        <KudosNotificationModule />
        <SyncMeetingsModalHandler />
        {newMeetingsModalFlag && pathAllowsNewMeetingsModal && <NewMeetingsModalHandler />}
        <div
          id="wp-sidebar-content"
          className={cn('wp-sidebar-content px-2 position-absolute pb-5 w-100', {
            'wp-sidebar-content--collapsed': !isExpanded,
          })}
        >
          <PILinkHeader />
          <ContentHeader setIsExpanded={setIsExpanded} navbarRef={navbarRef} />
          <ThirdPartyUserIndicator />
          <div
            className={cn('wp-sidebar-content-body navbar-nav flex-column sidebar-nav', {
              'wp-sidebar-content-body--collapsed': !isExpanded,
            })}
          >
            {!isZoom && !isTeamsEmbeddedTab && (
              <>
                {isExpanded && <SearchButton />}
                <BillingPlanTimerPopover
                  wrapperComponent={({ children }) => <div className="wp-sidebar-billing-timer">{children}</div>}
                />
                <ul className="navbar-nav sidebar-nav mt-3 w-100">
                  <HomePageV2Link onCloseNavbar={onCloseNavbar} />
                  {!isXtraLarge && <TodayScheduleNavLink handleCloseNavbar={onCloseNavbar} />}
                  {showCoachingMenuItem && (
                    <SideNavigationLink
                      to={PageUrls.COACHING}
                      label="Feedback"
                      iconClassName="whistle"
                      onClick={onCloseNavbar}
                    />
                  )}
                  <SideNavigationLink
                    to={PageUrls.GOALS_PAGE}
                    label="Goals"
                    iconClassName="bullseye-arrow"
                    onClick={onCloseNavbar}
                    isLocked={isGoalsLocked}
                    lockedDescription={ESSENTIALS_TOOLTIP}
                  />
                  {showAiToolkit && (
                    <SideNavigationLink
                      to={PageUrls.AI_TOOLKIT_PAGE}
                      label="AI Toolkit"
                      iconClassName="robot"
                      onClick={onCloseNavbar}
                    />
                  )}
                  {!isLarge && (
                    <SideNavigationLink
                      to={PageUrls.TEAM_PAGE}
                      label="Team Page"
                      iconClassName="user-friends"
                      onClick={onCloseNavbar}
                    />
                  )}
                  <NotificationsWrapper />
                  {showFeature('recommendation_analytics') && (
                    <SideNavigationLink
                      to={PageUrls.RECOMMENDATION_ANALYTICS}
                      label="Leaderboard"
                      iconClassName="chart-line"
                      onClick={handleRecommendationsAnalyticsClick}
                    />
                  )}
                  {showSurfaceAIItem && !isLarge && (
                    <SideNavigationLink to={PageUrls.SURFACEAI_PAGE} label="SurfaceAI" iconClassName="fal fa-magic" />
                  )}
                  {!isExpanded && <SearchButton />}
                  {showReviewsTab && <ReviewsMenuItem handleCloseNavbar={onCloseNavbar} />}
                  <SettingsMenuItem handleCloseNavbar={onCloseNavbar} />
                </ul>
              </>
            )}
            <div className="my-3">
              {isZoom || isTeamsEmbeddedTab ? (
                <CreateWorkspaceButton iconOnly={!isExpanded} onClick={onCloseNavbar} />
              ) : (
                <InviteTeammatesButton onCloseNavbar={onCloseNavbar} />
              )}
            </div>
            {!isWorkspaceFetching && <TeamGroupSection showFeature={showFeature} onCloseNavbar={onCloseNavbar} />}
          </div>
        </div>
        {isExpanded && <QuickStartGuide />}
      </nav>
    </ExpandNavbarContext.Provider>
  );
}

export default React.memo(SideNavigation);
